import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { TrendingUp, CheckCircle, Target, Globe } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.1 } }
};

export const WorkInAction = () => {
  const metrics = [
    {
      label: 'Years of Service',
      value: '30+',
      sub: 'Serving with integrity',
      icon: Target,
      testid: 'work-in-action-years-kpi'
    },
    {
      label: 'Businesses Guided',
      value: '10,000+',
      sub: 'Individuals & organizations',
      icon: TrendingUp,
      testid: 'work-in-action-clients-kpi'
    },
    {
      label: 'NC Offices',
      value: '4',
      sub: 'Across North Carolina',
      icon: Globe,
      testid: 'work-in-action-offices-kpi'
    },
    {
      label: 'On-Time Compliance',
      value: '99.3%',
      sub: 'Regulatory deadlines',
      icon: CheckCircle,
      testid: 'work-in-action-compliance-kpi'
    }
  ];

  const testimonials = [
    {
      quote: "KGOB's guidance helped us navigate complex tax challenges with confidence.",
      author: "CFO",
      company: "Manufacturing Client",
      testid: 'work-in-action-testimonial-1'
    },
    {
      quote: "The team brings both expertise and genuine care to every interaction.",
      author: "CEO",
      company: "TechStart Solutions",
      testid: 'work-in-action-testimonial-2'
    }
  ];

  return (
    <section 
      id="services"
      className="bg-[#F7F6F2] py-16 lg:py-20"
      data-testid="work-in-action-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.18em] text-xs text-[#4C5A70] mb-3">
            PROVEN RESULTS
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Our Work Speaks for Itself.
          </h2>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div 
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12"
        >
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <motion.div key={metric.label} variants={fadeUp}>
                <Card 
                  className="rounded-xl bg-white p-6 border border-[#E1DFD7] shadow-[0_1px_2px_rgba(11,34,64,0.06),0_8px_24px_rgba(11,34,64,0.06)] hover:shadow-[0_8px_30px_rgba(11,34,64,0.12)] transition-shadow duration-300 text-center"
                  data-testid={metric.testid}
                >
                  <div className="flex justify-center mb-3">
                    <Icon className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-1">
                    {metric.value}
                  </div>
                  <div className="text-sm font-medium text-[#0B2240] mb-1">
                    {metric.label}
                  </div>
                  <div className="text-xs text-[#4C5A70]">
                    {metric.sub}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Testimonials */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div key={index} variants={fadeUp}>
              <Card 
                className="rounded-2xl glass-card p-6 lg:p-8 h-full"
                data-testid={testimonial.testid}
              >
                <p className="text-lg text-[#0B2240] leading-relaxed mb-4 italic font-['Playfair_Display']">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-sm font-semibold text-[#0B2240]">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-[#4C5A70]">
                    {testimonial.company}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};